import {
	type IControlPointRetrivedData,
	type ISlopeBreak,
	type SegmentType,
	type TRubbingDestination,
	type ISavedTrenchData,
	PathSlopeType,
	type IGeometry,
	type ISurveyWGS84Point,
	type THorizontalAlignmentData,
	type TVerticalAlignmentData,
	HorizontalAlignmentType,
} from "earthbrain-viewer";
import {
	ObjectType,
	type ICrossSectionSettings,
	type IObject,
	type IObjectData,
} from "./types";
import { LongitudinalSlopeType } from "@/constant";
import { i18n } from "@/i18n.js";
import store from "@/store";
import { defaultLineWorksCrossSectionByDistanceUnit } from "@/components/Project/LineWorks/default";
import { Color, createGuid } from "cesium";
import type { ITrenchObjectData_Version2 } from "./TrenchObjectAdapter";
import { uploadObjAndNotify } from "@/utils/cesium-common";
import { md5 } from "hash-wasm";
import { distanceUnits, type DistanceUnit } from "@/models/siteUnit.types";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";

type TrenchDistanceDefaults = {
	width: number;
	interpPitch: number;
	cutTrenchHeight: number;
	cutShelfWidth: number;
	cutLeftSlopeHeight: number;
	cutRightSlopeHeight: number;
	cutLeftShelfWidth: number;
	cutRightShelfWidth: number;
};

const TRENCH_FEET_DISPLAY_DEFAULTS: TrenchDistanceDefaults = {
	width: 5,
	interpPitch: 16,
	cutTrenchHeight: 16,
	cutShelfWidth: 5,
	cutLeftSlopeHeight: 16,
	cutRightSlopeHeight: 16,
	cutLeftShelfWidth: 5,
	cutRightShelfWidth: 5,
};

/** Display defaults per distance unit (distance fields only; dimensionless params excluded) */
const TRENCH_DISPLAY_DEFAULTS: Record<DistanceUnit, TrenchDistanceDefaults> = {
	[distanceUnits.METERS]: {
		width: 1.5,
		interpPitch: 5,
		cutTrenchHeight: 5,
		cutShelfWidth: 1.5,
		cutLeftSlopeHeight: 5,
		cutRightSlopeHeight: 5,
		cutLeftShelfWidth: 1.5,
		cutRightShelfWidth: 1.5,
	},
	[distanceUnits.FEET]: TRENCH_FEET_DISPLAY_DEFAULTS,
	[distanceUnits.US_SURVEY_FEET]: TRENCH_FEET_DISPLAY_DEFAULTS,
};

export interface ITrenchObjectData extends IObjectData {
	id: string;
	name: string;
	panel: {
		trench: boolean;
		cut: boolean;
		volume: boolean;
	};
	cutVolume: number;
	transparency: number;
	length: number;
	heightRange: {
		min: number;
		max: number;
	};
	slopeRange: {
		min: number;
		max: number;
	};
	pathSettings: {
		width: number;
		leftSlope: number;
		rightSlope: number;
		isSuperElevation: boolean;
		interpolationPitch: number;
		surfaceColor: string;
		specifiedElevation: number;
		longitudinalSlope: {
			slopeType: string;
			slopeValue?: number;
			depth?: number | null;
		};
	};
	cutSettings: {
		trenchHeight: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		isLeftRightDifferent: boolean;
		generateShelf: boolean;
		leftSlopeHeight: number;
		leftMoundSlope: number;
		rightSlopeHeight: number;
		rightMoundSlope: number;
		leftShelfWidth: number;
		leftShelfSlope: number;
		rightShelfWidth: number;
		rightShelfSlope: number;
		color: string;
		outline: boolean;
	};
	associatedTerrain: { id: number; terrainType: string };
	rubbingDestination: TRubbingDestination;

	surveyPoints: ISurveyWGS84Point[];
	ipPointIds: string[];
	horizontalAlignment: THorizontalAlignmentData;
	verticalAlignment: TVerticalAlignmentData[];
	crossSectionSetting?: ICrossSectionSettings;
}

export class TrenchObject implements IObject, ITrenchObjectData {
	readonly type: ObjectType = ObjectType.TRENCH;
	panel: { trench: boolean; cut: boolean; volume: boolean };
	cutVolume: number;
	transparency: number;
	length: number;
	heightRange: { min: number; max: number };
	slopeRange: { min: number; max: number };
	pathSettings: {
		width: number;
		leftSlope: number;
		rightSlope: number;
		isSuperElevation: boolean;
		interpolationPitch: number;
		surfaceColor: string;
		specifiedElevation: number;
		longitudinalSlope: {
			slopeType: string;
			slopeValue?: number;
			depth?: number | null;
		};
	};
	cutSettings: {
		trenchHeight: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		isLeftRightDifferent: boolean;
		generateShelf: boolean;
		leftSlopeHeight: number;
		leftMoundSlope: number;
		rightSlopeHeight: number;
		rightMoundSlope: number;
		leftShelfWidth: number;
		leftShelfSlope: number;
		rightShelfWidth: number;
		rightShelfSlope: number;
		color: string;
		outline: boolean;
	};
	isLoaded: boolean;
	isHiddenDuringEdit: boolean;
	loadingVolume: boolean;
	controlPoints: IControlPointRetrivedData[];
	slopeBreaks: ISlopeBreak[];
	segmentTypes: SegmentType[];
	rubbingDestination: TRubbingDestination;
	id: string;
	md5: string;
	name: string;
	isOpen: boolean;
	associatedTerrain: { id: number; terrainType: string };

	surveyPoints: ISurveyWGS84Point[];
	ipPointIds: string[];
	horizontalAlignment: THorizontalAlignmentData;
	verticalAlignment: TVerticalAlignmentData[];
	crossSectionSetting: ICrossSectionSettings;

	isVisible: {
		ipLine: boolean;
		centerLine: boolean;
		crossSection: boolean;
		surface: boolean;
		cutEmbankment: boolean;
	};

	constructor() {
		this.isLoaded = false;
		this.isHiddenDuringEdit = false;
		this.isOpen = false;
		this.isVisible = {
			ipLine: false,
			centerLine: false,
			crossSection: false,
			surface: false,
			cutEmbankment: false,
		};
		this.loadingVolume = false;
		this.id = this.default.id;
		this.name = this.default.name;
		this.panel = this.default.panel;
		this.cutVolume = this.default.cutVolume;
		this.transparency = this.default.transparency;
		this.length = this.default.length;
		this.heightRange = this.default.heightRange;
		this.slopeRange = this.default.slopeRange;
		this.pathSettings = this.default.pathSettings;
		this.cutSettings = this.default.cutSettings;
		this.associatedTerrain = this.default.associatedTerrain;
		this.rubbingDestination = this.default.rubbingDestination;
		this.surveyPoints = [];
		this.ipPointIds = [];
		this.horizontalAlignment = this.default.horizontalAlignment;
		this.verticalAlignment = [];
		this.crossSectionSetting = this.default.crossSectionSetting;
	}

	get default(): ITrenchObjectData {
		const unit = store.state.site?.units?.distance ?? distanceUnits.METERS;
		const defaults = TRENCH_DISPLAY_DEFAULTS[unit];
		const unitPort = createDistanceUnitPort(unit);
		const toInternal = (v: number) => unitPort.toMeters(v);

		return {
			id: null,
			type: ObjectType.TRENCH,
			md5: "",
			name: "",
			panel: {
				trench: false,
				cut: false,
				volume: false,
			},
			cutVolume: 0,
			transparency: 1,
			length: 0,
			heightRange: { min: 0, max: 0 },
			slopeRange: { min: 0, max: 0 },
			pathSettings: {
				width: toInternal(defaults.width),
				leftSlope: 50,
				rightSlope: 50,
				isSuperElevation: false,
				interpolationPitch: toInternal(defaults.interpPitch),
				surfaceColor: "#964B00",
				specifiedElevation: null,
				longitudinalSlope: {
					slopeType: LongitudinalSlopeType.DEFAULT,
					slopeValue: 0,
					depth: null,
				},
			},
			cutSettings: {
				trenchHeight: toInternal(defaults.cutTrenchHeight),
				moundSlope: 0.1,
				shelfWidth: toInternal(defaults.cutShelfWidth),
				shelfSlope: 100,
				isLeftRightDifferent: false,
				generateShelf: false,
				leftSlopeHeight: toInternal(defaults.cutLeftSlopeHeight),
				leftMoundSlope: 0.1,
				rightSlopeHeight: toInternal(defaults.cutRightSlopeHeight),
				rightMoundSlope: 0.1,
				leftShelfWidth: toInternal(defaults.cutLeftShelfWidth),
				leftShelfSlope: 100,
				rightShelfWidth: toInternal(defaults.cutRightShelfWidth),
				rightShelfSlope: 100,
				color: "#FFA500",
				outline: true,
			},
			associatedTerrain: { id: null, terrainType: null },
			rubbingDestination: {
				target: null,
				id: null,
				specifiedAltitude: null,
			},
			surveyPoints: [],
			ipPointIds: [],
			horizontalAlignment: null,
			verticalAlignment: [],
			crossSectionSetting: defaultLineWorksCrossSectionByDistanceUnit(
				unit,
				toInternal,
			),
		};
	}
	get checkIfTrenchShouldBeUpdated() {
		return (
			this.horizontalAlignment?.type ===
			HorizontalAlignmentType.QUADRATIC_BEZIER
		);
	}

	get viewerLineId() {
		return this.id;
	}

	get lineName() {
		return this.name;
	}
	get isShow() {
		return (
			this.isVisible.centerLine ||
			this.isVisible.surface ||
			this.isVisible.cutEmbankment ||
			this.isVisible.crossSection
		);
	}

	get isEmbankmentVisible() {
		return this.isVisible.cutEmbankment;
	}

	get isAlignmentVisible() {
		return this.isVisible.ipLine || this.isVisible.centerLine;
	}

	async update(
		data: Partial<ITrenchObjectData>,
		updateInViewer: boolean = false,
	) {
		this.id = data.id ?? this.id;
		this.name = data.name ?? this.name ?? this.default.name;
		this.panel.trench = data.panel.trench ?? this.panel.trench;
		this.panel.cut = data.panel.cut ?? this.panel.cut;
		this.panel.volume = data.panel.volume ?? this.panel.volume;
		this.cutVolume = data.cutVolume ?? this.cutVolume ?? this.default.cutVolume;
		this.transparency =
			data.transparency ?? this.transparency ?? this.default.transparency;
		this.length = data.length ?? this.length ?? this.default.length;
		this.heightRange =
			data.heightRange ?? this.heightRange ?? this.default.heightRange;
		this.slopeRange =
			data.slopeRange ?? this.slopeRange ?? this.default.slopeRange;
		this.pathSettings.width =
			data.pathSettings.width ??
			this.pathSettings.width ??
			this.default.pathSettings.width;
		this.pathSettings.leftSlope =
			data.pathSettings.leftSlope ??
			this.pathSettings.leftSlope ??
			this.default.pathSettings.leftSlope;
		this.pathSettings.rightSlope =
			data.pathSettings.rightSlope ??
			this.pathSettings.rightSlope ??
			this.default.pathSettings.rightSlope;
		this.pathSettings.isSuperElevation =
			data.pathSettings.isSuperElevation ??
			this.pathSettings.isSuperElevation ??
			this.default.pathSettings.isSuperElevation;
		this.pathSettings.interpolationPitch =
			data.pathSettings.interpolationPitch ??
			this.pathSettings.interpolationPitch ??
			this.default.pathSettings.interpolationPitch;
		this.pathSettings.surfaceColor =
			data.pathSettings.surfaceColor ??
			this.pathSettings.surfaceColor ??
			this.default.pathSettings.surfaceColor;
		this.pathSettings.specifiedElevation =
			data.pathSettings.specifiedElevation ??
			this.pathSettings.specifiedElevation ??
			this.default.pathSettings.specifiedElevation;
		this.pathSettings.longitudinalSlope =
			data.pathSettings.longitudinalSlope ??
			this.pathSettings.longitudinalSlope ??
			this.default.pathSettings.longitudinalSlope;
		this.cutSettings.trenchHeight =
			data.cutSettings.trenchHeight ??
			this.cutSettings.trenchHeight ??
			this.default.cutSettings.trenchHeight;
		this.cutSettings.moundSlope =
			data.cutSettings.moundSlope ??
			this.cutSettings.moundSlope ??
			this.default.cutSettings.moundSlope;
		this.cutSettings.shelfWidth =
			data.cutSettings.shelfWidth ??
			this.cutSettings.shelfWidth ??
			this.default.cutSettings.shelfWidth;
		this.cutSettings.shelfSlope =
			data.cutSettings.shelfSlope ??
			this.cutSettings.shelfSlope ??
			this.default.cutSettings.shelfSlope;
		this.cutSettings.isLeftRightDifferent =
			data.cutSettings.isLeftRightDifferent ??
			this.cutSettings.isLeftRightDifferent ??
			this.default.cutSettings.isLeftRightDifferent;
		this.cutSettings.generateShelf =
			data.cutSettings.generateShelf ??
			this.cutSettings.generateShelf ??
			this.default.cutSettings.generateShelf;
		this.cutSettings.leftSlopeHeight =
			data.cutSettings.leftSlopeHeight ??
			this.cutSettings.leftSlopeHeight ??
			this.default.cutSettings.leftSlopeHeight;
		this.cutSettings.leftMoundSlope =
			data.cutSettings.leftMoundSlope ??
			this.cutSettings.leftMoundSlope ??
			this.default.cutSettings.leftMoundSlope;
		this.cutSettings.rightSlopeHeight =
			data.cutSettings.rightSlopeHeight ??
			this.cutSettings.rightSlopeHeight ??
			this.default.cutSettings.rightSlopeHeight;
		this.cutSettings.rightMoundSlope =
			data.cutSettings.rightMoundSlope ??
			this.cutSettings.rightMoundSlope ??
			this.default.cutSettings.rightMoundSlope;
		this.cutSettings.leftShelfWidth =
			data.cutSettings.leftShelfWidth ??
			this.cutSettings.leftShelfWidth ??
			this.default.cutSettings.leftShelfWidth;
		this.cutSettings.leftShelfSlope =
			data.cutSettings.leftShelfSlope ??
			this.cutSettings.leftShelfSlope ??
			this.default.cutSettings.leftShelfSlope;
		this.cutSettings.rightShelfWidth =
			data.cutSettings.rightShelfWidth ??
			this.cutSettings.rightShelfWidth ??
			this.default.cutSettings.rightShelfWidth;
		this.cutSettings.rightShelfSlope =
			data.cutSettings.rightShelfSlope ??
			this.cutSettings.rightShelfSlope ??
			this.default.cutSettings.rightShelfSlope;
		this.cutSettings.color =
			data.cutSettings.color ??
			this.cutSettings.color ??
			this.default.cutSettings.color;
		this.cutSettings.outline =
			data.cutSettings.outline ??
			this.cutSettings.outline ??
			this.default.cutSettings.outline;
		this.associatedTerrain.id =
			data.associatedTerrain.id ??
			this.associatedTerrain.id ??
			this.default.associatedTerrain.id;
		this.associatedTerrain.terrainType =
			data.associatedTerrain.terrainType ??
			this.associatedTerrain.terrainType ??
			this.default.associatedTerrain.terrainType;
		this.surveyPoints =
			data.surveyPoints ?? this.surveyPoints ?? this.default.surveyPoints;
		this.ipPointIds =
			data.ipPointIds ?? this.ipPointIds ?? this.default.ipPointIds;
		this.horizontalAlignment =
			data.horizontalAlignment ??
			this.horizontalAlignment ??
			this.default.horizontalAlignment;
		this.verticalAlignment =
			data.verticalAlignment ??
			this.verticalAlignment ??
			this.default.verticalAlignment;
		this.rubbingDestination =
			data.rubbingDestination ??
			this.rubbingDestination ??
			this.default.rubbingDestination;

		this.pathSettings.rightSlope = this.pathSettings.isSuperElevation
			? -1 * this.pathSettings.leftSlope
			: this.pathSettings.leftSlope;

		if (!this.cutSettings.isLeftRightDifferent) {
			this.cutSettings.leftSlopeHeight = this.cutSettings.trenchHeight;
			this.cutSettings.rightSlopeHeight = this.cutSettings.trenchHeight;

			this.cutSettings.leftMoundSlope = this.cutSettings.moundSlope;
			this.cutSettings.rightMoundSlope = this.cutSettings.moundSlope;

			this.cutSettings.leftShelfSlope = this.cutSettings.shelfSlope;
			this.cutSettings.rightShelfSlope = this.cutSettings.shelfSlope;

			this.cutSettings.leftShelfWidth = this.cutSettings.shelfWidth;
			this.cutSettings.rightShelfWidth = this.cutSettings.shelfWidth;
		}

		this.crossSectionSetting =
			data.crossSectionSetting ??
			this.crossSectionSetting ??
			this.default.crossSectionSetting;

		if (updateInViewer) {
			await this.updateSettingsInViewer();
		}
	}

	getSettingsForViewer() {
		const getLongitudinalSlope = () => {
			if (
				this.pathSettings.longitudinalSlope.slopeType ===
				LongitudinalSlopeType.DEFAULT
			) {
				return {
					slopeType: LongitudinalSlopeType.DEFAULT,
				};
			}
			if (
				this.pathSettings.longitudinalSlope.slopeType ===
				LongitudinalSlopeType.CONSTANT
			) {
				return {
					slopeType: LongitudinalSlopeType.CONSTANT,
					slopeValue: this.pathSettings.longitudinalSlope.slopeValue,
				};
			}
			if (
				this.pathSettings.longitudinalSlope.slopeType ===
				LongitudinalSlopeType.FLAT
			) {
				return {
					slopeType: LongitudinalSlopeType.FLAT,
					slopeValue: this.pathSettings.longitudinalSlope.slopeValue,
				};
			}
			if (
				this.pathSettings.longitudinalSlope.slopeType ===
				LongitudinalSlopeType.NONE
			) {
				return {
					slopeType: LongitudinalSlopeType.NONE,
					depth: this.pathSettings.longitudinalSlope.depth,
				};
			}
		};
		const commonPathSetting = {
			specifiedElevation: this.pathSettings.specifiedElevation,
			leftWidth: this.pathSettings.width / 2,
			rightWidth: this.pathSettings.width / 2,
			interpolationPitch: this.pathSettings.interpolationPitch,
			color: this.pathSettings.surfaceColor,
			transparency: this.transparency,
			longitudinalSlope: getLongitudinalSlope(),
			controlLineSettings: {
				width: 1,
				color: Color.SKYBLUE.withAlpha(0.5),
				outlineColor: Color.RED,
				outlineWidth: 3,
				clampToGround: false,
				epsilonGapBetWeenMeshAndPolyline: 0.1,
			},
			polyLineSettings: {
				width: 9,
				color: Color.SKYBLUE.withAlpha(0.5),
				outlineColor: Color.BLUE,
				outlineWidth: 3,
				clampToGround: false,
				epsilonGapBetWeenMeshAndPolyline: 0.1,
			},
		};
		const settingsForViewer = {
			commonSetting: {
				path: this.pathSettings.isSuperElevation
					? {
							...commonPathSetting,
							trenchSlopeType: PathSlopeType.STRAIGHT,
							slope: this.pathSettings.leftSlope,
						}
					: {
							...commonPathSetting,
							trenchSlopeType: PathSlopeType.CROSS,
							leftSlope: this.pathSettings.leftSlope,
							rightSlope: this.pathSettings.rightSlope,
						},

				cutSetting: {
					generateShelf: this.cutSettings.generateShelf,
					left: {
						slopeHeight: this.cutSettings.leftSlopeHeight,
						moundSlope: this.cutSettings.leftMoundSlope,
						shelfWidth: this.cutSettings.leftShelfWidth,
						shelfSlope: this.cutSettings.leftShelfSlope,
					},
					right: {
						slopeHeight: this.cutSettings.rightSlopeHeight,
						moundSlope: this.cutSettings.rightMoundSlope,
						shelfWidth: this.cutSettings.rightShelfWidth,
						shelfSlope: this.cutSettings.rightShelfSlope,
					},
					color: this.cutSettings.color,
					transparency: this.transparency,
				},
			},
		};
		return settingsForViewer;
	}

	public async updateMaterialSettings(updateInViewer: boolean = false) {
		if (updateInViewer) {
			await store.getters.viewer.updateTrenchMaterialSettings(
				this.id,
				{ surfaceColor: this.pathSettings.surfaceColor },
				{ color: this.cutSettings.color },
			);
		}
	}

	public async updateTransparency() {
		await store.getters.viewer.updateTrenchMaterialSettings(
			this.id,
			{},
			{ transparency: this.transparency },
		);
	}

	getViewerJSON(): ISavedTrenchData {
		return {
			trenchId: this.id,
			trenchSettings: this.getSettingsForViewer() as any,
			surveyPoints: this.surveyPoints,
			ipPointIds: this.ipPointIds,
			horizontalAlignment: this.horizontalAlignment,
			verticalAlignment: this.verticalAlignment,
			associatedTerrain: {
				id: this.associatedTerrain.id,
				terrainType: this.associatedTerrain.terrainType as any,
			},
			rubbingDestination: this.rubbingDestination,
		};
	}

	async loadInViewer(): Promise<void> {
		try {
			this.id = await store.getters.viewer.loadTrenchJSON(
				this.getViewerJSON(),
				this.crossSectionSetting.interpolationPitch,
			);
			this.updateFromViewer();
			this.isLoaded = true;
			this.isHiddenDuringEdit = false;
			this.isVisible = {
				ipLine: true,
				centerLine: false,
				crossSection: false,
				surface: true,
				cutEmbankment: true,
			};
		} catch (e) {
			console.error("Failed to load trench in viewer:", e);
			this.isVisible = {
				ipLine: false,
				centerLine: false,
				crossSection: false,
				surface: false,
				cutEmbankment: false,
			};
			this.isLoaded = false;
			this.isHiddenDuringEdit = false;
		}
	}

	private async loadTrench() {
		await this.loadInViewer();
		if (!this.isLoaded) {
			// Loading failed; do not attempt to toggle visibility
			return;
		}
		if (!this.crossSectionSetting.id) {
			const savedCrossSection = await store.dispatch(
				"lineWorksCrossSection/saveCrossSectionOnCreate",
				{
					object: this,
					siteId: store.getters.site.site_uuid,
					crossSectionParams: this.crossSectionSetting,
				},
				{ root: true },
			);
			this.crossSectionSetting.id = savedCrossSection.cross_section_id;
		}
	}

	private shouldBlockRestoreOnInvalidEditForm(): boolean {
		return (
			store.state.object.objectUnderEdit?.id === this.id &&
			store.state.object.isEditFormValid === false
		);
	}

	private showInvalidEditFormSnackbar(): void {
		store.commit("set_snackbar", {
			text: i18n.global.t("CANNOT_SHOW_OBJECT_INVALID_VALUES"),
			color: "rgba(153, 0, 0, 0.72)",
			timeout: -1,
		});
	}

	private hideAllVisibles() {
		store.getters.viewer.setTrenchVisibility(this.id, false);
		this.isVisible = {
			ipLine: false,
			centerLine: false,
			crossSection: false,
			surface: false,
			cutEmbankment: false,
		};
	}

	async setVisibility(visible: boolean): Promise<boolean> {
		if (visible && !this.isLoaded) {
			if (this.shouldBlockRestoreOnInvalidEditForm()) {
				this.showInvalidEditFormSnackbar();
				return false;
			}
			await this.loadTrench();
			if (!this.isLoaded) {
				return false;
			}
		}
		store.getters.viewer.setTrenchVisibility(this.id, visible);
		this.isVisible.surface = visible;
		this.isVisible.cutEmbankment = visible;
		this.isVisible.ipLine = visible;
		if (!visible) {
			await this.setCenterLineVisibility(false);
		}
		if (!visible && store.state.object.objectUnderEdit?.id === this.id) {
			this.isHiddenDuringEdit = true;
		}
		return true;
	}

	async startEdit(): Promise<void> {
		// ask user if data needs to be updated to straight or default
		let result: string | null = null;
		if (this.checkIfTrenchShouldBeUpdated) {
			result = await store.dispatch("object/openObjectUpdateDialog");
			if (result === null) {
				throw new Error("User cancelled to update old trench", {
					cause: {
						code: "CANCELLED_BY_USER",
						message: "Trench cannot be edited unless updated to new trench",
					},
				});
			}
		}
		store.getters.viewer.enableTrenchEdit(
			true,
			this.id,
			async (_id, sectionMarkerLabel) => {
				this.updateFromViewer();
				// undefined means "label unchanged"; only endEdit clears it.
				if (typeof sectionMarkerLabel === "string") {
					store.commit("setActiveSectionMarkerLabel", sectionMarkerLabel);
				}
			},
		);
		result &&
			store.getters.viewer.updateTrenchHorizontalAlignmentType(this.id, result);
	}
	async endEdit() {
		if (this.isVisible.crossSection) {
			await this.setCrossSectionVisibility(false);
		}
		await store.getters.viewer.updateAlignmentStationInterval(
			this.id,
			this.crossSectionSetting.interpolationPitch,
		);
		store.getters.viewer.enableTrenchEdit(false, this.id, () => {});
		store.commit("setActiveSectionMarkerLabel", "");
		this.updateFromViewer();
		const propsJSON = await this.toJSON();
		await uploadObjAndNotify(propsJSON, this.type);
		await store.dispatch(
			"lineWorksCrossSection/saveCrossSectionOfLineWorks",
			{
				crossSectionSetting: {
					...this.crossSectionSetting,
					name: this.name,
				},
			},
			{ root: true },
		);
	}

	deleteObject(): void {
		store.getters.viewer.deleteTrench(this.id);
	}
	getGeometry(): IGeometry {
		return store.getters.viewer.getTrenchMeshGeometry(this.id);
	}

	fitToView(): void {
		store.getters.viewer.fitToTrench(this.id);
	}

	async setCenterLineVisibility(visible: boolean) {
		if (this.isVisible.centerLine === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadTrench();
			this.hideAllVisibles();
		}
		await store.getters.viewer.setTrenchCenterLineVisibility(this.id, visible);
		this.isVisible.centerLine = visible;
		if (!this.isVisible.centerLine) {
			await this.setCrossSectionVisibility(false);
		}
	}

	async setIpLineVisibility(visible: boolean) {
		if (this.isVisible.ipLine === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadTrench();
			this.hideAllVisibles();
		}
		await store.getters.viewer.setTrenchIpLineVisibility(this.id, visible);
		this.isVisible.ipLine = visible;
	}

	async setAlignmentVisibility(visible: boolean) {
		if (this.isAlignmentVisible === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadTrench();
			this.hideAllVisibles();
		}
		await this.setIpLineVisibility(visible);
		await this.setCenterLineVisibility(visible);
	}

	async setCrossSectionVisibility(visible: boolean) {
		if (this.isVisible.crossSection === visible) return;
		if (visible) {
			await this.setVisibility(true);
			await this.setCenterLineVisibility(true);
			await store.getters.viewer.generateAlignmentCrossSection(
				this.id,
				this.crossSectionSetting,
			);
			this.isVisible.crossSection = visible;
		} else {
			await store.getters.viewer.disposeLineWorksCrossSection(this.id);
			this.isVisible.crossSection = visible;
		}
	}

	async setSurfaceVisibility(visible: boolean) {
		if (this.isVisible.surface === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadTrench();
			this.hideAllVisibles();
		}
		await store.getters.viewer.setTrenchSurfaceVisibility(this.id, visible);
		this.isVisible.surface = visible;
	}

	async setEmbankmentVisibility(visible: boolean) {
		if (this.isVisible.cutEmbankment === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadTrench();
			this.hideAllVisibles();
		}
		await store.getters.viewer.setTrenchEmbankmentVisibility(this.id, visible);
		this.isVisible.cutEmbankment = visible;
	}

	async updateCrossSection() {
		await store.getters.viewer.generateAlignmentCrossSection(
			this.id,
			this.crossSectionSetting,
		);
	}

	updateFromViewer() {
		const lengthOfTrench = store.getters.viewer.getLengthOfTrench(this.id);
		if (lengthOfTrench) {
			this.length = Number.parseFloat(lengthOfTrench.toFixed(3));
		}
		this.heightRange = store.getters.viewer.getTrenchHeightInfo(this.id);
		this.slopeRange = store.getters.viewer.getTrenchSlopeInfo(this.id);

		const trenchData: ISavedTrenchData = store.getters.viewer.getTrenchJSON(
			this.id,
		);
		if (!trenchData) {
			return;
		}
		this.surveyPoints = trenchData.surveyPoints;
		this.ipPointIds = trenchData.ipPointIds;
		this.horizontalAlignment = trenchData.horizontalAlignment;
		this.verticalAlignment = trenchData.verticalAlignment;
	}

	async updateRubbingDestination(targetType, id, specifiedAltitude) {
		this.rubbingDestination.target = targetType;
		if (this.rubbingDestination.target === "specified-altitude") {
			this.rubbingDestination.specifiedAltitude = specifiedAltitude;
			this.rubbingDestination.id = null;
		} else {
			this.rubbingDestination.target = targetType;
			this.rubbingDestination.id = id;
		}
		await this.updateSettingsInViewer();
	}

	async updateSettingsInViewer() {
		await store.getters.viewer.updateTrenchSettings(
			this.id,
			this.getSettingsForViewer(),
			this.rubbingDestination,
		);

		this.updateFromViewer();
	}
	async toJSON(): Promise<ITrenchObjectData_Version2> {
		this.updateFromViewer();
		const json = {
			id: this.id,
			cid: this.id,
			type: this.type,
			name: this.name,
			panel: this.panel,
			cutVolume: this.cutVolume,
			transparency: this.transparency,
			length: this.length,
			heightRange: this.heightRange,
			slopeRange: this.slopeRange,
			pathSettings: this.pathSettings,
			cutSettings: this.cutSettings,
			associatedTerrain: this.associatedTerrain,
			surveyPoints: this.surveyPoints,
			ipPointIds: this.ipPointIds,
			horizontalAlignment: this.horizontalAlignment,
			verticalAlignment: this.verticalAlignment,
			rubbingDestination: this.rubbingDestination,
		};
		let hash: string;
		try {
			hash = await md5(JSON.stringify(json));
		} catch (error) {
			console.error("Failed to generate MD5 hash:", error);
			hash = "M85FailedToGenerateMD5Hash"; // Or generate a fallback identifier
		}

		return {
			version: 2.0,
			...json,
			md5: hash,
		};
	}

	async toDuplicateJSON(): Promise<ITrenchObjectData_Version2> {
		this.updateFromViewer();
		const surveyIdMap = new Map<string, string>();
		this.surveyPoints.forEach((surveyPoint) => {
			surveyIdMap.set(surveyPoint.id, createGuid());
		});
		const newSurveyPoints = this.surveyPoints.map((surveyPoint) => {
			return {
				...surveyPoint,
				id: surveyIdMap.get(surveyPoint.id),
			};
		});
		const newIpPointIds = this.ipPointIds.map((ipPointId) => {
			return surveyIdMap.get(ipPointId);
		});
		const newId = crypto.randomUUID();
		const json = {
			id: newId,
			cid: newId,
			type: this.type,
			name: this.name + " copy",
			panel: this.panel,
			cutVolume: this.cutVolume,
			transparency: this.transparency,
			length: this.length,
			heightRange: this.heightRange,
			slopeRange: this.slopeRange,
			pathSettings: this.pathSettings,
			cutSettings: this.cutSettings,
			associatedTerrain: this.associatedTerrain,
			surveyPoints: newSurveyPoints,
			ipPointIds: newIpPointIds,
			horizontalAlignment: this.horizontalAlignment,
			verticalAlignment: this.verticalAlignment,
			rubbingDestination: this.rubbingDestination,
		};
		let hash: string;
		try {
			hash = await md5(JSON.stringify(json));
		} catch (error) {
			console.error("Failed to generate MD5 hash:", error);
			hash = "M85FailedToGenerateMD5Hash"; // Or generate a fallback identifier
		}

		return {
			version: 2.0,
			...json,
			md5: hash,
		};
	}
}
